import React, { useState, useEffect } from 'react';
import { Modal, Popover } from 'antd';
import { CloseOutlined, LeftOutlined, FolderOutlined, DownOutlined } from '@ant-design/icons';
import CustomButton from '../Button';
import styles from './MoveToModal.module.scss';
import { useSecureService } from '../../store/service';
import { parseApiUtcToLocalDate } from '../../utils/apiDateTime';

export type MoveToModalMode = 'move' | 'duplicate';

export interface MoveToModalProps {
  visible: boolean;
  onCancel: () => void;
  /** Taşınacak / kopyalanacak öğe */
  entryToMove?: { id: number; name: string; path?: string; entryType?: string };
  /** Çoklu seçim: taşınacak/kopyalanacak öğeler (hedef listesinden hariç tutulur) */
  bulkEntries?: { id: number; name: string; path?: string; entryType?: string }[];
  /** Başlangıçta gösterilecek klasör path'i (genelde mevcut sayfa path'i = üst klasör) */
  initialPath: string;
  /** Onay: hedef path ve hedef entry */
  onMove: (targetPath: string, targetEntry?: any) => void | Promise<void>;
  loading?: boolean;
  variant?: 'internal' | 'external';
  mode?: MoveToModalMode;
}

const MoveToModal: React.FC<MoveToModalProps> = ({
  visible,
  onCancel,
  entryToMove,
  bulkEntries,
  initialPath,
  onMove,
  loading = false,
  variant = 'internal',
  mode = 'move',
}) => {
  const isDuplicateMode = mode === 'duplicate';
  const bulkCount = bulkEntries?.length ?? 0;
  const modalTitle = isDuplicateMode
    ? bulkCount > 1
      ? `Duplicate ${bulkCount} items to...`
      : 'Duplicate to...'
    : bulkCount > 1
      ? `Move ${bulkCount} items to...`
      : 'Move entry to...';
  const confirmLabel = isDuplicateMode ? 'Duplicate' : 'Move';
  const { getEntryByPath, getBreadcrumb, getProjects } = useSecureService();
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [entry, setEntry] = useState<any>(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState<{ name: string; path: string }[]>([]);
  const [loadingData, setLoadingData] = useState(false);

  const normalized = (p: string) => (p || '').trim().replace(/^\/+/, '');
  const pathNorm = normalized(currentPath);
  const firstSegment = pathNorm.split('/')[0] || pathNorm || '';
  const rootSegment = firstSegment === 'ext' || firstSegment === 'int'
    ? firstSegment
    : (variant === 'external' ? 'ext' : 'int');
  const isExt = firstSegment === 'ext' || pathNorm.startsWith('ext/');
  const isInt = firstSegment === 'int' || pathNorm.startsWith('int/');
  const isRootProjectsLevel = (firstSegment === 'ext' || firstSegment === 'int') && pathNorm === firstSegment;
  const projectsScope = firstSegment === 'ext' ? 'External' : 'Internal';

  useEffect(() => {
    if (visible) {
      setCurrentPath(initialPath);
    }
  }, [visible, initialPath]);

  useEffect(() => {
    if (!visible) return;
    if (!pathNorm) {
      setEntry(null);
      setBreadcrumbItems([]);
      setLoadingData(false);
      return;
    }
    let cancelled = false;
    setLoadingData(true);
    (async () => {
      try {
        if (isRootProjectsLevel) {
          const resProjects = await getProjects(projectsScope);
          if (cancelled) return;
          const projectItems = resProjects?.data?.items;
          const projects = Array.isArray(projectItems) ? projectItems : [];
          setEntry({
            entryType: 'ScopeRoot',
            path: firstSegment,
            children: projects.map((project: any) => ({
              ...project,
              id: project?.projectId ?? project?.id,
              projectId: project?.projectId ?? project?.id,
              path: normalized(project?.path || `${firstSegment}/${project?.id || project?.name}`),
              entryType: 'Project',
            })),
          });
          setBreadcrumbItems([]);
          return;
        }

        const [resEntry, resBreadcrumb] = await Promise.all([getEntryByPath(pathNorm), getBreadcrumb(pathNorm)]);
        if (cancelled) return;
        const entryData = resEntry?.data ?? (resEntry?.success !== false ? resEntry : null);
        setEntry(entryData && typeof entryData === 'object' ? entryData : null);
        const items = resBreadcrumb?.success
          ? (resBreadcrumb as { data?: { items?: unknown[] } }).data?.items
          : undefined;
        setBreadcrumbItems(
          Array.isArray(items)
            ? items.map((item: any) => ({ name: item.name || '', path: item.url || pathNorm }))
            : []
        );
      } catch {
        if (!cancelled) {
          setEntry(null);
          setBreadcrumbItems([]);
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, currentPath, firstSegment, isRootProjectsLevel, pathNorm, projectsScope]);

  const currentPathNorm = pathNorm;
  const rootName = rootSegment === 'ext' ? 'External projects' : 'Internal projects';
  const rootItem: { name: string; path: string } = { name: rootName, path: rootSegment };
  const displayBreadcrumb = [rootItem, ...breadcrumbItems];

  const breadcrumbLabel = displayBreadcrumb.length > 0
    ? displayBreadcrumb.map((i) => i.name).join(' › ')
    : pathNorm || '—';

  const breadcrumbParent = displayBreadcrumb.length > 1 ? displayBreadcrumb[displayBreadcrumb.length - 2] : null;
  const canGoPrevious = !!breadcrumbParent;

  const excludedEntries =
    bulkEntries && bulkEntries.length > 0
      ? bulkEntries
      : entryToMove
        ? [entryToMove]
        : [];
  const folders = Array.isArray(entry?.children)
    ? entry.children.filter((c: any) => {
        const isProject = c.entryType === 'Project' || String(c.entryType) === 'Project';
        const isFolder = c.entryType === 'Folder' || String(c.entryType) === 'Folder';
        if (isRootProjectsLevel) return isProject;
        if (!isFolder) return false;
        if (excludedEntries.length === 0) return true;
        const childPath = c.path || `${currentPathNorm}/${c.id ?? c.name}`;
        const isExcluded = excludedEntries.some((ex) => {
          const exPath = ex.path ? normalized(ex.path) : '';
          return c.id === ex.id || (exPath && childPath === exPath);
        });
        return !isExcluded;
      })
    : [];

  const handlePrevious = () => {
    if (breadcrumbParent) setCurrentPath(breadcrumbParent.path);
  };

  const handleFolderClick = (child: any) => {
    const childPath = child.path || `${normalized(currentPath)}/${child.id || child.name}`;
    setCurrentPath(childPath);
  };

  const handleConfirm = async () => {
    if (!entry?.id) return;
    try {
      await onMove(currentPath, entry);
      onCancel();
    } catch {
      // parent shows error; modal stays open
    }
  };

  const maxVisible = 4;
  const showEllipsis = displayBreadcrumb.length > maxVisible;
  const firstItem = displayBreadcrumb[0];
  const lastTwo = showEllipsis ? displayBreadcrumb.slice(-2) : [];
  const middleItems = showEllipsis ? displayBreadcrumb.slice(1, -2) : [];

  const renderBreadcrumbSegments = () => {
    if (displayBreadcrumb.length === 0) return null;
    const breadcrumbPopoverClassName = `${styles.breadcrumbPopover} ${variant === 'external' ? styles.breadcrumbPopoverExternal : ''}`;
    const rootSelector = (
      <Popover
        trigger="click"
        placement="bottomLeft"
        overlayClassName={breadcrumbPopoverClassName}
        content={
          <div className={styles.breadcrumbPopoverContent}>
            <button
              type="button"
              className={styles.breadcrumbPopoverItem}
              onClick={() => setCurrentPath('int')}
            >
              Internal projects
            </button>
            <button
              type="button"
              className={styles.breadcrumbPopoverItem}
              onClick={() => setCurrentPath('ext')}
            >
              External projects
            </button>
          </div>
        }
      >
        <button type="button" className={styles.breadcrumbLink}>
          {rootItem.name || '—'} <DownOutlined />
        </button>
      </Popover>
    );
    const lastIndex = displayBreadcrumb.length - 1;
    if (!showEllipsis) {
      return displayBreadcrumb.map((item, index) => {
        const isCurrent = index === lastIndex;
        if (index === 0) {
          return (
            <span key={`${item.path}-${index}`} className={styles.breadcrumbChunk}>
              {rootSelector}
            </span>
          );
        }
        return (
          <span key={`${item.path}-${index}`} className={styles.breadcrumbChunk}>
            {index > 0 && <span className={styles.breadcrumbSep}> › </span>}
            <button
              type="button"
              className={isCurrent ? `${styles.breadcrumbLink} ${styles.breadcrumbLinkCurrent}` : styles.breadcrumbLink}
              onClick={() => setCurrentPath(item.path)}
            >
              {item.name || '—'}
            </button>
          </span>
        );
      });
    }
    const lastTwoLastIndex = lastTwo.length - 1;
    return (
      <>
        <span className={styles.breadcrumbChunk}>
          {rootSelector}
        </span>
        <span className={styles.breadcrumbChunk}>
          <span className={styles.breadcrumbSep}> › </span>
          <Popover
            trigger="click"
            placement="bottomLeft"
            overlayClassName={breadcrumbPopoverClassName}
            content={
              <div className={styles.breadcrumbPopoverContent}>
                {middleItems.map((item, idx) => (
                  <button
                    key={`${item.path}-${idx}`}
                    type="button"
                    className={styles.breadcrumbPopoverItem}
                    onClick={() => setCurrentPath(item.path)}
                  >
                    {item.name || '—'}
                  </button>
                ))}
              </div>
            }
          >
            <button type="button" className={styles.breadcrumbLink}>...</button>
          </Popover>
        </span>
        {lastTwo.map((item, i) => {
          const isCurrent = i === lastTwoLastIndex;
          return (
            <span key={`${item.path}-${i}`} className={styles.breadcrumbChunk}>
              <span className={styles.breadcrumbSep}> › </span>
              <button
                type="button"
                className={isCurrent ? `${styles.breadcrumbLink} ${styles.breadcrumbLinkCurrent}` : styles.breadcrumbLink}
                onClick={() => setCurrentPath(item.path)}
              >
                {item.name || '—'}
              </button>
            </span>
          );
        })}
      </>
    );
  };

  return (
    <Modal
      centered
      title={
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>{modalTitle}</span>
          <CloseOutlined onClick={onCancel} className={styles.closeIcon} />
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      className={`${styles.moveToModal} ${variant === 'external' ? styles.moveToModalExternal : ''}`}
      width={520}
    >
      <div className={styles.modalContent}>
        <div className={styles.content}>
          <div className={styles.previousRow}>
            <button
              type="button"
              className={styles.previousLink}
              onClick={handlePrevious}
              disabled={!canGoPrevious}
            >
              <LeftOutlined className={styles.previousLinkIcon} />
              Previous
            </button>
          </div>

          <div className={styles.breadcrumbRow}>
            {displayBreadcrumb.length > 0 ? (
              <div className={styles.breadcrumbList}>
                {renderBreadcrumbSegments()}
              </div>
            ) : (
              <div className={styles.breadcrumbText}>{breadcrumbLabel}</div>
            )}
          </div>

          {loadingData ? (
            <div className={styles.emptyFolders}>Loading...</div>
          ) : folders.length === 0 ? (
            <div className={styles.emptyFolders}>No folders here.</div>
          ) : (
            <div className={styles.folderTableWrap}>
              <ul className={styles.folderList}>
                {folders.map((child: any) => {
                  const dateRaw = child.updatedAt ?? child.createdAt;
                  const d = dateRaw ? parseApiUtcToLocalDate(dateRaw) : null;
                  const dateStr = d
                    ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—';
                  return (
                    <li
                      key={child.id}
                      className={styles.folderItem}
                      onClick={() => handleFolderClick(child)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleFolderClick(child)}
                    >
                      <span className={styles.folderColName}>
                        <FolderOutlined className={styles.folderItemIcon} />
                        {child.name}
                      </span>
                      <span className={styles.folderColCount}>—</span>
                      <span className={styles.folderColDate}>{dateStr}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className={styles.actionButtons}>
          <CustomButton onClick={onCancel} className={styles.cancelButton}>
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleConfirm}
            loading={loading}
            disabled={!entry?.id || loadingData}
            className={styles.moveButton}
          >
            {confirmLabel}
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default MoveToModal;
